import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

/**
 * Enriches all books currently in user libraries using ISBNdb as primary source
 * This targets the 53+ books that users actually have in their libraries
 */
export async function enrichUserLibraryBooks() {
  try {
    console.log('[UserLibraryEnrichment] Starting targeted enrichment...');
    
    toast.info('Starting enrichment of user library books...');

    // Get all unique book IDs from user_books
    const { data: userBooks, error: userBooksError } = await supabase
      .from('user_books')
      .select('book_id')
      .not('book_id', 'is', null);

    if (userBooksError) {
      throw userBooksError;
    }

    // Extract unique book IDs
    const uniqueBookIds = [...new Set(userBooks?.map(ub => ub.book_id) || [])];
    
    console.log(`[UserLibraryEnrichment] Found ${uniqueBookIds.length} unique books in user libraries`);

    if (uniqueBookIds.length === 0) {
      toast.info('No books found in user libraries to enrich');
      return { success: true, booksProcessed: 0 };
    }

    // Process books in batches to avoid timeouts
    const batchSize = 10;
    let totalProcessed = 0;
    let totalEnriched = 0;
    let totalFailed = 0;
    const allErrors: string[] = [];

    toast.info(`Starting enrichment of ${uniqueBookIds.length} books in batches...`);

    for (let i = 0; i < uniqueBookIds.length; i += batchSize) {
      const batch = uniqueBookIds.slice(i, i + batchSize);
      
      console.log(`[UserLibraryEnrichment] Processing batch ${Math.floor(i / batchSize) + 1} of ${Math.ceil(uniqueBookIds.length / batchSize)}`);
      
      try {
        const { data, error } = await supabase.functions.invoke('enrich-books', {
          body: {
            action: 'enrich-books',
            bookIds: batch,
            prioritizeIsbndb: true,
            languageFilter: 'en',
            extractSeries: true,
            batchSize: batchSize
          }
        });

        if (error) {
          console.error(`[UserLibraryEnrichment] Batch error:`, error);
          allErrors.push(error.message);
          totalFailed += batch.length;
          continue;
        }

        totalProcessed += data.processed || 0;
        totalEnriched += data.enriched || 0;
        totalFailed += data.failed || 0;
        
        if (data.errors && data.errors.length > 0) {
          allErrors.push(...data.errors);
        }

        // Update progress
        toast.info(`Progress: ${totalProcessed} / ${uniqueBookIds.length} books processed`);
      } catch (err) {
        console.error('[UserLibraryEnrichment] Batch processing error:', err);
        totalFailed += batch.length;
      }
    }

    console.log('[UserLibraryEnrichment] All batches completed:', {
      totalProcessed,
      totalEnriched,
      totalFailed,
      errors: allErrors
    });
    
    if (totalFailed > 0) {
      toast.warning(`Enriched ${totalEnriched} books, ${totalFailed} books couldn't be enriched (likely missing ISBNs)`, {
        description: 'Books without ISBNs may need manual data entry.',
        duration: 8000
      });
    } else {
      toast.success(`Successfully enriched ${totalEnriched} out of ${uniqueBookIds.length} books!`, {
        description: 'Books now have better metadata, synopses, and series information.',
        duration: 6000
      });
    }
    
    return {
      success: true,
      booksProcessed: totalProcessed,
      booksEnriched: totalEnriched,
      booksFailed: totalFailed,
      errors: allErrors
    };
  } catch (error) {
    console.error('[UserLibraryEnrichment] Failed:', error);
    toast.error('Failed to enrich user library books', {
      description: error instanceof Error ? error.message : 'Unknown error occurred'
    });
    throw error;
  }
}

/**
 * Gets enrichment statistics for user library books
 */
export async function getUserLibraryEnrichmentStats() {
  try {
    // Get all unique books in user libraries with their enrichment status
    const { data, error } = await supabase
      .from('user_books')
      .select(`
        book_id,
        books!fk_user_books_book_id (
          id,
          title,
          synopsis,
          series_name,
          page_count,
          enrichment_status,
          enrichment_confidence,
          language,
          isbn13,
          isbn10
        )
      `)
      .not('book_id', 'is', null);

    if (error) {
      throw error;
    }

    const books = data?.map(ub => ub.books).filter(Boolean) || [];
    const uniqueBooks = Array.from(new Map(books.map(b => [b.id, b])).values());

    const stats = {
      total: uniqueBooks.length,
      withSynopsis: uniqueBooks.filter(b => b.synopsis && b.synopsis.length > 50).length,
      withSeries: uniqueBooks.filter(b => b.series_name).length,
      withPageCount: uniqueBooks.filter(b => b.page_count && b.page_count > 0).length,
      withISBN: uniqueBooks.filter(b => b.isbn13 || b.isbn10).length,
      complete: uniqueBooks.filter(b => 
        b.synopsis && 
        b.page_count && 
        b.language === 'en' &&
        b.enrichment_status === 'completed'
      ).length,
      pending: uniqueBooks.filter(b => 
        b.enrichment_status === 'pending' || !b.enrichment_status
      ).length,
      nonEnglish: uniqueBooks.filter(b => b.language && b.language !== 'en').length
    };

    return stats;
  } catch (error) {
    console.error('[UserLibraryEnrichment] Failed to get stats:', error);
    throw error;
  }
}
